/* PerfectTed: Our story
   Requires gsap, ScrollTrigger and (optionally) SplitText, loaded before this file. */
(function () {
  'use strict';

  var SELECTOR = '[data-pts]';
  var instances = new Map();

  function ready() {
    return window.gsap && window.ScrollTrigger;
  }

  /* Lighter animation inside the theme editor */
  function isDesign() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* The theme scrolls .js-page-wrapper (not the window) from 990px up */
  function getScroller() {
    var wrap = document.querySelector('.js-page-wrapper');
    if (wrap && /(auto|scroll)/.test(window.getComputedStyle(wrap).overflowY)) return wrap;
    return window;
  }

  /* Height of the sticky header, so pins start beneath it. 0 if the header is not sticky.
     The header is a section in #header-group that is sticky or fixed at the top. */
  function headerOffset() {
    var group = document.querySelector('#header-group');
    if (!group) return 0;
    var offset = 0;
    Array.prototype.forEach.call(group.children, function (el) {
      var cs = window.getComputedStyle(el);
      if ((cs.position === 'sticky' || cs.position === 'fixed') && parseFloat(cs.top) === 0) {
        /* The wrapper is taller than the bar at the very top of the page, so measure the bar */
        var bar = el.querySelector('.s-header-custom__inner');
        offset = Math.max(offset, bar ? bar.offsetHeight : el.offsetHeight);
      }
    });
    return offset;
  }

  function registerPlugins() {
    var plugins = [window.ScrollTrigger];
    if (window.SplitText) plugins.push(window.SplitText);
    window.gsap.registerPlugin.apply(window.gsap, plugins);
  }

  /* ---------- HERO ---------- */
  function setupHero(root, state, isDesktop) {
    var gsap = window.gsap;
    var hero = root.querySelector('[data-pts-hero]');
    if (!hero) return;

    var win = hero.querySelector('[data-pts-window]');
    var copy = hero.querySelector('[data-pts-copy]');
    var scrim = hero.querySelector('.pts-hero__scrim');
    var title = hero.querySelector('[data-pts-title]');
    var floats = Array.prototype.slice.call(hero.querySelectorAll('[data-pts-float]'));

    if (window.SplitText && title && !isDesign()) {
      var split = window.SplitText.create(title, { type: 'words', mask: 'words' });
      state.splits.push(split);
      gsap.from(split.words, { yPercent: 110, duration: 1.1, ease: 'power4.out', stagger: 0.08, delay: 0.15 });
    }

    var tl = gsap.timeline({
      scrollTrigger: { trigger: hero, start: function () { return 'top top+=' + headerOffset(); }, end: 'bottom bottom', scrub: isDesign() ? true : 0.6 }
    });

    tl.fromTo(win,
      { clipPath: isDesktop
        ? 'inset(38% 38% 6% 38% round 999px 999px 40px 40px)'
        : 'inset(50% 10% 5% 10% round 999px 999px 24px 24px)' },
      { clipPath: 'inset(0% 0% 0% 0% round 0px 0px 0px 0px)', ease: 'none' }, 0)
      .to(copy, { yPercent: -60, autoAlpha: 0, ease: 'none', duration: 0.5 }, 0)
      .to(scrim, { opacity: 1, ease: 'none', duration: 0.4 }, 0.6);

    floats.forEach(function (el, i) {
      var speed = parseFloat(el.getAttribute('data-pts-float')) || 0.4;
      tl.to(el, {
        /* Travel far enough to leave the top of the stage instead of parking against it */
        y: function () { return -Math.max(window.innerHeight * speed * 1.4, el.offsetTop + el.offsetHeight * 1.3); },
        rotation: i % 2 ? 14 : -14,
        ease: 'none'
      }, 0);
    });

    /* Wiggle: a small sway on the image itself (the wrapper is busy with travel and rotation),
       driven by scroll progress so it settles when scrolling stops. */
    if (floats.length) {
      var wigglers = floats.map(function (el, i) {
        return { img: el.querySelector('img'), phase: i * 1.7, amp: 4 + i };
      }).filter(function (w) { return w.img; });
      window.ScrollTrigger.create({
        trigger: hero,
        start: function () { return 'top top+=' + headerOffset(); },
        end: 'bottom bottom',
        onUpdate: function (self) {
          wigglers.forEach(function (w) {
            gsap.to(w.img, {
              rotation: Math.sin(self.progress * Math.PI * 12 + w.phase) * w.amp,
              x: Math.cos(self.progress * Math.PI * 9 + w.phase) * 6,
              duration: 0.4,
              ease: 'power2.out',
              overwrite: 'auto'
            });
          });
        }
      });
    }

    if (isDesktop && !isDesign() && window.matchMedia('(pointer: fine)').matches && floats.length) {
      var movers = floats.map(function (el) {
        return {
          x: gsap.quickTo(el, 'x', { duration: 0.8, ease: 'power3' }),
          depth: (parseFloat(el.getAttribute('data-pts-float')) || 0.4) * 60
        };
      });
      var onMove = function (e) {
        var nx = e.clientX / window.innerWidth - 0.5;
        movers.forEach(function (m) { m.x(nx * m.depth); });
      };
      hero.addEventListener('pointermove', onMove);
      state.cleanups.push(function () { hero.removeEventListener('pointermove', onMove); });
    }
  }

  /* ---------- TIMELINE: horizontal (desktop, optionally mobile) ---------- */
  function setupHorizontal(root, state) {
    var gsap = window.gsap;
    var track = root.querySelector('[data-pts-track]');
    var pin = root.querySelector('[data-pts-pin]');
    var rail = root.querySelector('[data-pts-rail]');
    if (!track || !pin || !rail) return;

    var panels = Array.prototype.slice.call(rail.querySelectorAll('[data-pts-year]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-pts-dot]'));
    var fill = root.querySelector('[data-pts-fill]');
    var base = { bg: getComputedStyle(document.body).getPropertyValue('--color-background').trim(), fg: getComputedStyle(document.body).getPropertyValue('--color-foreground').trim() };
    var pauseScreens = parseFloat(root.getAttribute('data-pts-pause'));
    if (isNaN(pauseScreens)) pauseScreens = 0.8;

    function distance() { return Math.max(0, rail.scrollWidth - window.innerWidth); }
    /* Scroll the user spends sitting on the first and on the last year */
    function hold() { return pauseScreens * window.innerHeight; }

    /* The pin is native CSS sticky (a transform pin lags behind scrolling inside the theme's
       scroll container and makes the section bob). The track is as tall as the pin plus the
       scroll it needs: a pause on the first year, the sideways travel, a pause on the last year. */
    function sizeTrack() { track.style.height = (pin.offsetHeight + distance() + 2 * hold()) + 'px'; }
    sizeTrack();
    window.ScrollTrigger.addEventListener('refreshInit', sizeTrack);
    state.cleanups.push(function () {
      window.ScrollTrigger.removeEventListener('refreshInit', sizeTrack);
      track.style.height = '';
    });

    function paint(bg, fg) {
      gsap.to(root, { '--pts-bg': bg, '--pts-fg': fg, duration: 0.7, ease: 'power2.out', overwrite: 'auto' });
    }

    var current = -1;
    function activate(i) {
      if (i === current) return;
      current = i;
      var p = panels[i];
      paint(p.getAttribute('data-bg'), p.getAttribute('data-fg'));
      dots.forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
    }

    function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

    /* Rail position, active year and per-panel parallax all follow one number: how far the
       rail has travelled (0 to distance). Holds at either end keep it at 0 or distance. */
    function render(progress) {
      var d = distance();
      var vw = window.innerWidth;
      /* Sideways drift of the big numeral, small enough to keep the whole number inside its panel */
      var drift = vw >= 900 ? 14 : 6;
      var m = clamp(progress * (d + 2 * hold()) - hold(), 0, d);

      gsap.set(rail, { x: -m });
      if (fill) gsap.set(fill, { scaleX: d ? m / d : 0 });
      activate(clamp(Math.floor((m + 0.45 * vw) / vw), 0, panels.length - 1));

      panels.forEach(function (p, i) {
        var q = clamp((vw - (i * vw - m)) / (2 * vw), 0, 1);
        var num = p.querySelector('.pts-year__num');
        var cutout = p.querySelector('[data-pts-cutout]');
        if (num) gsap.set(num, { xPercent: drift - 2 * drift * q });
        if (cutout) gsap.set(cutout, { y: 120 - 160 * q, rotation: -10 + 16 * q });
      });
    }

    var proxy = { p: 0 };
    var tween = gsap.to(proxy, {
      p: 1,
      ease: 'none',
      onUpdate: function () { render(proxy.p); },
      scrollTrigger: {
        trigger: track,
        start: function () { return 'top top+=' + headerOffset(); },
        end: 'bottom bottom',
        scrub: isDesign() ? true : 0.8,
        invalidateOnRefresh: true,
        onEnter: function () { current = -1; render(proxy.p); },
        onLeaveBack: function () { current = -1; paint(base.bg, base.fg); },
        onLeave: function () { current = -1; paint(base.bg, base.fg); },
        onEnterBack: function () { current = -1; render(proxy.p); },
        onRefresh: function () { render(proxy.p); }
      }
    });
    state.pinTrigger = tween.scrollTrigger;
    state.panelCount = panels.length;
    state.hold = hold;
    render(0);
  }

  /* ---------- TIMELINE: small screens ---------- */
  function setupVertical(root) {
    var gsap = window.gsap;
    root.querySelectorAll('[data-pts-year]').forEach(function (p) {
      var num = p.querySelector('.pts-year__num');
      if (!num) return;
      gsap.fromTo(num, { yPercent: 30 }, {
        yPercent: -30, ease: 'none',
        scrollTrigger: { trigger: p, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* ---------- CLOSING ---------- */
  function setupClosing(root, state) {
    var gsap = window.gsap;
    var close = root.querySelector('[data-pts-close]');
    if (!close) return;

    var media = close.querySelector('[data-pts-close-media]');
    if (media) {
      gsap.fromTo(media, { scale: 1.15 }, {
        scale: 1, ease: 'none',
        scrollTrigger: { trigger: close, start: 'top bottom', end: 'bottom bottom', scrub: true }
      });
    }

    var line = close.querySelector('[data-pts-close-line]');
    if (line && window.SplitText && !isDesign()) {
      var split = window.SplitText.create(line, { type: 'lines', mask: 'lines' });
      state.splits.push(split);
      gsap.from(split.lines, {
        yPercent: 110, duration: 1, ease: 'power3.out', stagger: 0.1,
        scrollTrigger: { trigger: line, start: 'top 75%' }
      });
    }
  }

  /* ---------- LIFECYCLE ---------- */
  function init(root, attempt) {
    if (!ready()) {
      /* Scripts injected by the theme editor load asynchronously, so wait briefly */
      if ((attempt || 0) < 50) setTimeout(function () { init(root, (attempt || 0) + 1); }, 100);
      return;
    }
    destroy(root);
    registerPlugins();

    var state = { splits: [], cleanups: [], pinTrigger: null, panelCount: 0, scroller: null, hold: null };
    var mm = window.gsap.matchMedia();

    mm.add({
      motion: '(prefers-reduced-motion: no-preference)',
      desktop: '(min-width: 900px)',
      wide: '(min-width: 990px)' /* scroller changes here, so re-run when crossing it */
    }, function (ctx) {
      if (!ctx.conditions.motion) return;
      var isDesktop = ctx.conditions.desktop;
      var scroller = getScroller();
      var scrollEls = [document.documentElement];
      if (scroller !== window) scrollEls.push(scroller);
      var prevBehaviour = scrollEls.map(function (el) { return el.style.scrollBehavior; });
      /* Smooth scroll behaviour fights scrubbed animation */
      scrollEls.forEach(function (el) { el.style.scrollBehavior = 'auto'; });
      window.ScrollTrigger.defaults({ scroller: scroller });
      state.scroller = scroller;
      root.style.setProperty('--pts-offset', headerOffset() + 'px');
      /* Horizontal timeline on desktop, and on mobile when the section setting asks for it */
      var isHorizontal = isDesktop || root.getAttribute('data-pts-mobile') === 'horizontal';
      root.classList.add('pts--motion');
      if (isHorizontal) root.classList.add('pts--h');

      setupHero(root, state, isDesktop);
      if (isHorizontal) setupHorizontal(root, state); else setupVertical(root);
      setupClosing(root, state);

      return function () {
        scrollEls.forEach(function (el, i) { el.style.scrollBehavior = prevBehaviour[i]; });
        window.ScrollTrigger.defaults({ scroller: window });
        state.scroller = null;
        state.splits.forEach(function (s) { s.revert(); });
        state.cleanups.forEach(function (fn) { fn(); });
        state.splits = [];
        state.cleanups = [];
        state.pinTrigger = null;
        root.classList.remove('pts--motion', 'pts--h');
        root.style.removeProperty('--pts-bg');
        root.style.removeProperty('--pts-fg');
        root.style.removeProperty('--pts-offset');
      };
    });

    instances.set(root, { mm: mm, state: state });
  }

  function destroy(root) {
    var inst = instances.get(root);
    if (!inst) return;
    inst.mm.revert();
    instances.delete(root);
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(function (root) { init(root); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', function () {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { if (window.ScrollTrigger) window.ScrollTrigger.refresh(); });
    }
  });

  /* ---------- THEME EDITOR ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector(SELECTOR);
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector(SELECTOR);
    if (root) destroy(root);
  });

  document.addEventListener('shopify:block:select', function (e) {
    var panel = e.target.closest('[data-pts-year]');
    var root = e.target.closest(SELECTOR);
    if (!panel || !root) return;
    var inst = instances.get(root);
    var st = inst && inst.state.pinTrigger;
    if (!st || inst.state.panelCount < 2) {
      panel.scrollIntoView({ block: 'start' });
      return;
    }
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-pts-year]'));
    var i = panels.indexOf(panel);
    /* Scroll distance equals the track length: the opening pause, then one viewport per year */
    var pause = inst.state.hold ? inst.state.hold() : 0;
    (inst.state.scroller || window).scrollTo({ top: st.start + pause + i * window.innerWidth, behavior: 'instant' });
  });
})();
